import { useEffect, useState } from 'react';
import { API_E2E_GET_AASO_BY_APPLICANT } from 'src/api/e2e-request-objects';
import {
  E2eBaseCallDetailsI,
  e2eGetBasePublicRequests,
} from 'src/api/query-logic/e2e-query-base-calls';
import { apiRequest } from 'src/api/request-handler';
import {
  breadHandleCreateAccountRoutingLogic,
  breadHandleE2eRoutingLogic,
  getFullE2ePath,
} from 'src/e2e-redesign/business-logic/bread-logic/bread-routing-logic';
import { e2eConfigLogic_buildStoreLenderLogoObjects } from 'src/e2e-redesign/business-logic/e2e-configuration-object-logic';
import { E2eLogicObjectsWithParams } from 'src/e2e-redesign/business-logic/e2e-logic-utils';
import {
  E2eRoutingArgsI,
  handleCreateAccountRoutingLogic,
  handleE2eRoutingLogic,
  handleE2eUnauthenticatedRoutingLogic,
} from 'src/e2e-redesign/business-logic/e2e-routing-logic';
import { Logo } from 'src/e2e-redesign/components/LogoGrid';
import { ComponentLogicI } from 'src/e2e-redesign/interfaces/e2e-base-interfaces';
import useE2eStore from 'src/e2e-store';
import { ApplicationStage } from 'src/enums/aaso.enums';
import { E2ERoutes, NewE2ERoutes } from 'src/enums/routes.enums';
import { E2eGlobalStore } from 'src/interfaces/e2e-state.interfaces';
import { routeTransitionMap } from 'src/interfaces/transition-maps';
import { BREAD_LENDER_ID } from 'src/utils/bread-e2e-constants';
import { fsTrackEvent } from 'src/utils/fullstory';

export const welcomeLogic_init = (routingPackage: E2eLogicObjectsWithParams) => {
  const params = routingPackage.params;
  const { store_uuid, e2e_form_id, store_name } = params;
  const { e2eStore } = routingPackage;
  const baseCallDetails: E2eBaseCallDetailsI = {
    store_uuid: params.store_uuid,
    e2e_form_id: params.e2e_form_id,
  };
  //we are unauthing the user so no need to get authed calls ahead of time.
  useEffect(() => {
    // If an applicant is on the welcome page and there's no sourceId or paymentType in URL params, clear them from localStorage
    const searchParams = new URLSearchParams(window.location.search);
    const sourceId = searchParams.get('source');
    const paymentType = searchParams.get('paymentType');

    if (!sourceId) {
      window.localStorage.removeItem('source');
    }
    if (!paymentType) {
      window.localStorage.removeItem('paymentType');
    }
  }, []);
  // this runs if user is not authenticated
  e2eGetBasePublicRequests(e2eStore, baseCallDetails, false);
  // this runs if they are
  // e2eGetBaseAuthenticatedRequests(baseCallDetails, e2eStore, isAuthenticated);

  useEffect(() => {
    if (store_uuid && store_name && e2e_form_id) {
      e2eStore.initApplicationStore(store_uuid, store_name, e2e_form_id);
    }
  }, []);
};

export interface WelcomLogicI extends ComponentLogicI {
  fn: {
    handleLogin: () => void;
    handleAASORedirect: () => void;
    handleStartNew: () => void;
    handleContinue: (continueRoute: any | undefined, continueAASO: any) => void;
    handleCreateAccount: () => void;
  };
  v: {
    continueAASO: any;
    continueRoute: string;
    openContinueApp: boolean;
    disclosureLogos: Logo[];
    loading: boolean;
    hasError: boolean;
    errorMessage: string;
  };
}

export const welcomLogic_funcAndV = (routingPackage: E2eLogicObjectsWithParams): WelcomLogicI => {
  const { params, auth, location } = routingPackage;
  const { e2e_form_id, store_uuid, store_name } = params;
  const createAaso = useE2eStore((state: E2eGlobalStore) => state.createAaso);
  const setAaso = useE2eStore((state: E2eGlobalStore) => state.setAASO);
  const user = auth?.user;
  const e2eStore = routingPackage.e2eStore;
  const app_configs = e2eStore.app_configurations;
  const [openContinueApp, setOpenContinueApp] = useState<boolean>(false);
  const [continueRoute, setContinueRoute] = useState<any>();
  const [disclosureLogos, setDisclosureLogos] = useState<Logo[]>([]);
  const [continueAASO, setContinueAASO] = useState<any>();
  const [loading, setLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  
  const baseCallDetails: E2eBaseCallDetailsI = {
    store_uuid,
    e2e_form_id,
  };
  const publicRequests = e2eGetBasePublicRequests(e2eStore, baseCallDetails, false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('e_id')) {
      window.localStorage.setItem('e_id', params.get('e_id') as string);
    } else if (params.get('p_id')) {
      window.localStorage.setItem('p_id', params.get('p_id') as string);
    }
  }, [location.search]);

  useEffect(() => {
    if (auth?.isAuthenticated) {
      Promise.resolve(auth.signOut()).then(() => {
        window.location.href = window.location.href;
      });
    }
  }, []);

  // Check for application config errors or loading state
  useEffect(() => {
    if (publicRequests.appConfigIsError) {
      setHasError(true);
      setErrorMessage('This is a broken form link. Please contact your location associate for more assistance.');
      setLoading(false);
    } else if (!publicRequests.appConfigIsLoading && app_configs && !auth?.isAuthenticated) {
      // Data has loaded successfully and user is not authenticated
      setLoading(false);
    }
  }, [publicRequests.appConfigIsError, publicRequests.appConfigIsLoading, app_configs, auth?.isAuthenticated]);

  useEffect(() => {
    if (app_configs?.lender_ids) {
      const dls = e2eConfigLogic_buildStoreLenderLogoObjects(
        app_configs,
        e2eStore.disclosures,
        app_configs?.lender_ids,
      );
      setDisclosureLogos(dls);
    }
  }, [app_configs, e2eStore.disclosures]);

  useEffect(() => {
    if (app_configs?.store_details) {
      fsTrackEvent('Welcome Page Landing', {
        store_name,
        store_uuid,
        store_id: app_configs?.store_details?.id,
      });
    }
  }, [app_configs]);

  const handleCreateAccount = async () => {
    const lenderLineup = app_configs?.lender_ids;
    if (lenderLineup && lenderLineup.includes(BREAD_LENDER_ID)) {
      breadHandleCreateAccountRoutingLogic(routingPackage, {
        currentStage: ApplicationStage.STARTED,
        currentE2eRoute: NewE2ERoutes.CreateAccount,
      });
    } else {
      handleCreateAccountRoutingLogic(routingPackage, {
        currentStage: ApplicationStage.STARTED,
        currentE2eRoute: NewE2ERoutes.CreateAccount,
      });
    }
  };

  const handleShowContinue = (continueRoute: string, continueAASO: any) => {
    setContinueAASO(continueAASO);
    setOpenContinueApp(true);
    setContinueRoute(continueRoute);
  };

  const handleContinue = (continueRoute: any | undefined, continueAASO: any) => {
    const newRoutingPackage = {
      ...routingPackage,
      aasoContext: { aaso: continueAASO },
    };
    setAaso(continueAASO);
    const routingArgs: E2eRoutingArgsI = {
      currentStage: continueAASO.current_stage,
      currentE2eRoute: E2ERoutes.Welcome, // this doesn't matter
      aaso: continueAASO,
      continueRoute,
      continue: true,
    };
    let hasBread = false;
    const lenderIds = continueAASO?.e2e_form_snapshot?.paths?.reduce((acc: number[], path: any) => {
      return [...acc, ...path.lenders];
    }, []);

    if (lenderIds && lenderIds.includes(BREAD_LENDER_ID)) {
      hasBread = true;
    }

    if (hasBread) {
      breadHandleE2eRoutingLogic(newRoutingPackage, routingArgs, continueAASO);
    } else {
      handleE2eRoutingLogic(newRoutingPackage, routingArgs, continueAASO);
    }
  };

  const handleStartNew = async () => {
    if (store_uuid && e2e_form_id && user) {
      const generatedAASO = await createAaso(user, store_uuid, e2e_form_id);
      const lenderLineup = app_configs?.lender_ids;
      if (lenderLineup && lenderLineup.includes(BREAD_LENDER_ID)) {
        const routingArgs: E2eRoutingArgsI = {
          currentStage: ApplicationStage.STARTED,
          currentE2eRoute: E2ERoutes.Welcome,
        };
        handleE2eRoutingLogic(routingPackage, routingArgs, generatedAASO);
      } else {
        const routingArgs: E2eRoutingArgsI = {
          currentStage: ApplicationStage.STARTED,
          currentE2eRoute: E2ERoutes.Welcome,
        };
        handleE2eRoutingLogic(routingPackage, routingArgs, generatedAASO);
      }
    }
  };

  const handleCreateNewAASOForPaymentOptions = async () => {
    if (store_uuid && e2e_form_id && user) {
      const generatedAASO = await createAaso(user, store_uuid, e2e_form_id);
      if (
        generatedAASO &&
        generatedAASO.store_name &&
        generatedAASO.store_uuid &&
        generatedAASO._id &&
        generatedAASO.e2e_form_id
      ) {
        const path = getFullE2ePath(
          generatedAASO.store_name,
          generatedAASO.store_uuid,
          generatedAASO._id,
          generatedAASO.e2e_form_id,
          NewE2ERoutes.PaymentOptions,
        );
        routingPackage.navigate(path);
        return;
      }
      // fallback
      routingPackage.navigate('/dashboard');
    }
  };

  // const handleAASORedirect = async () => {
  //   if (user) {
  //     let AASO;
  //     try {
  //       if (store_uuid && e2e_form_id) {
  //         // check to see if an existing aaso already exists
  //         AASO = await apiRequest(API_E2E_GET_AASO_BY_APPLICANT, {
  //           params: { applicant_id: user?.id, store_uuid, e2e_form_id },
  //         });
  //       }
  //     } catch (e) {
  //     } finally {
  //       // aaso does not exists, create new
  //       if (!AASO?._id) return handleStartNew();

  //       if (AASO?._id) {
  //         const paymentType = window.localStorage.getItem('paymentType');
  //         if (paymentType) {
  //           const { store_name, store_uuid, _id, e2e_form_id } = AASO;
  //           console.log('Redirecting to PaymentOptions with:', {
  //             store_name,
  //             store_uuid,
  //             aaso_id: _id,
  //             e2e_form_id,
  //             AASO,
  //           });
  //           if (store_name && store_uuid && _id && e2e_form_id) {
  //             const path = getFullE2ePath(store_name, store_uuid, _id, e2e_form_id, NewE2ERoutes.PaymentOptions);
  //             console.log('path', path);
  //             routingPackage.navigate(path);
  //             return;
  //           }
  //         }
  //         // aaso already exists
  //         const routeFromStage = routeTransitionMap[AASO.current_stage as keyof typeof routeTransitionMap];
  //         if (routeFromStage) {
  //           return handleShowContinue(routeFromStage, { ...AASO });
  //         }
  //       } else {
  //         // navigate('dashboard');
  //       }
  //     }
  //   }
  // };

  const handleAASORedirect = async () => {
    if (user) {
      let AASO;
      const source = window.localStorage.getItem('source');
      const paymentType = window.localStorage.getItem('paymentType');
      try {
        if (store_uuid && e2e_form_id) {
          AASO = await apiRequest(API_E2E_GET_AASO_BY_APPLICANT, {
            params: { applicant_id: user?.id, store_uuid, e2e_form_id },
          });
        }
        console.log('AASO initial block', AASO);
      } catch (e) {
      } finally {
        if (source && paymentType) {
          if (!AASO?._id) return await handleCreateNewAASOForPaymentOptions();
          // If AASO exists and source_id matches, redirect to PaymentOptions
          if (AASO.source_id === source) {
            const { store_name, store_uuid, _id, e2e_form_id } = AASO;
            if (store_name && store_uuid && _id && e2e_form_id) {
              const path = getFullE2ePath(store_name, store_uuid, _id, e2e_form_id, NewE2ERoutes.PaymentOptions);
              routingPackage.navigate(path);
              return;
            }
          }
          // If AASO exists and source_id does NOT match, create new and redirect
          if (AASO.source_id !== source) return await handleCreateNewAASOForPaymentOptions();
          return routingPackage.navigate('/dashboard');
        }
        if (!AASO?._id) return handleStartNew();

        if (AASO?._id) {
          const routeFromStage = routeTransitionMap[AASO.current_stage as keyof typeof routeTransitionMap];
          if (routeFromStage) {
            return handleShowContinue(routeFromStage, { ...AASO });
          }
        }
        // navigate('dashboard'))
      }
    }
  };

  const handleLogin = () => {
    if (auth?.isAuthenticated) {
      return handleAASORedirect();
    }
    const routingArgs: E2eRoutingArgsI = {
      currentStage: ApplicationStage.STARTED,
      currentE2eRoute: E2ERoutes.Welcome,
    };
    handleE2eUnauthenticatedRoutingLogic(routingPackage, routingArgs);
  };

  return {
    fn: {
      handleLogin,
      handleAASORedirect,
      handleStartNew,
      handleContinue,
      handleCreateAccount,
    },
    v: {
      continueAASO,
      continueRoute,
      openContinueApp,
      disclosureLogos,
      loading,
      hasError,
      errorMessage,
    },
  };
};
